import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, Save, Loader2, RotateCcw, CheckCircle2 } from "lucide-react";

export function AdminWaWebhookSettingsCard() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [url, setUrl] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.waWebhookGet();
      setData(res.data);
      setUrl(res.data.active_url || "");
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load webhook setting");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminAPI.waWebhookGet();
        if (!active) return;
        setData(res.data);
        setUrl(res.data.active_url || "");
        setDirty(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load webhook setting");
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.waWebhookUpdate(url.trim());
      setData(res.data);
      setUrl(res.data.active_url || "");
      setDirty(false);
      toast.success("Webhook URL saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.waWebhookUpdate(""); // empty → falls back to env
      setData(res.data);
      setUrl(res.data.active_url || "");
      setDirty(false);
      toast.success("Reset to default (env)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = (() => {
    if (!data) return null;
    if (data.source === "db") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-100 text-indigo-700">
          <CheckCircle2 className="h-3 w-3" /> Custom (DB override)
        </span>
      );
    }
    if (data.source === "env") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">
          Default (env)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-100 text-red-700">
        Not configured
      </span>
    );
  })();

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="wa-webhook-settings">
      <div className="flex items-center gap-2 mb-3">
        <Link2 className="h-4 w-4 text-slate-600" />
        <span className="text-sm font-semibold text-[#18181B]">WASimple Flow Webhook URL</span>
        {sourceBadge}
      </div>

      <p className="text-[11px] text-slate-500 mb-2">
        All WA Flow triggers POST to this URL. Leave empty to fall back to the env-configured default.
      </p>

      {loading && !data ? (
        <div className="py-4 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <input
            type="text"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setDirty(true); }}
            placeholder="https://app.wasimple.in/api/v1/chatbot/webhook/<uuid>"
            className="w-full h-9 px-2.5 rounded-md text-xs font-mono bg-white border border-slate-200 outline-none focus:border-indigo-500/50"
            data-testid="wa-webhook-input"
          />

          <div className="flex items-center justify-between mt-3">
            <div className="text-[11px] text-slate-500 space-y-0.5">
              {data?.updated_at && (
                <p>
                  Last updated: {new Date(data.updated_at).toLocaleString()} by{" "}
                  <span className="font-mono">{data.updated_by || "—"}</span>
                </p>
              )}
              {data?.env_url && data?.source === "db" && (
                <p className="font-mono text-slate-400 truncate max-w-md">
                  env fallback: {data.env_url}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {data?.source === "db" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving}
                  data-testid="wa-webhook-reset-btn"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to env
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || !dirty}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="wa-webhook-save-btn"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                )}
                Save
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
